import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from './supabase';

const pageMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
};

function LogoMark({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="10" fill="url(#rrLogoGrad)" />
      <path
        d="M20 6L8 11v10c0 7.18 5.13 13.89 12 15.5C27.87 34.89 33 28.18 33 21V11L20 6z"
        fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1"
      />
      <path d="M14 20.5l4 4 8-8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <defs>
        <linearGradient id="rrLogoGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2D7DD2" /><stop offset="1" stopColor="#1A4E8A" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function ResetPassword({ onDone }) {
  const [sessionValid, setSessionValid] = useState(null); // null = checking, true/false = result
  const [password, setPassword]         = useState('');
  const [confirm, setConfirm]           = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [done, setDone]                 = useState(false);

  // Validate that there is an active recovery session before showing the form
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Supabase sets the session from the recovery token; if no session exists
      // the link is invalid, expired, or already used.
      setSessionValid(!!session);
    });
  }, []);

  const passwordChecks = [
    { label: 'At least 12 characters',          met: password.length >= 12          },
    { label: 'One lowercase letter',            met: /[a-z]/.test(password)         },
    { label: 'One uppercase letter',            met: /[A-Z]/.test(password)         },
    { label: 'One number',                      met: /[0-9]/.test(password)         },
    { label: 'One special character (! @ # $)', met: /[^A-Za-z0-9]/.test(password) },
  ];
  const passwordValid = passwordChecks.every(c => c.met);

  async function handleSubmit() {
    setError('');
    if (!passwordValid) { setError('Please meet all password requirements.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message);
      setLoading(false);
      return;
    }

    // Sign out immediately so they must log in manually with the new password
    await supabase.auth.signOut();
    setDone(true);
    setLoading(false);
  }

  const inputStyle = {
    width: '100%', background: 'var(--surface)', border: '1px solid var(--border2)',
    borderRadius: 7, padding: '10px 13px', fontSize: 13, color: 'var(--text)',
    fontFamily: 'var(--font)', outline: 'none', transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  };

  // Still checking session
  if (sessionValid === null) {
    return (
      <motion.div {...pageMotion} style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>Verifying reset link…</div>
      </motion.div>
    );
  }

  // Invalid / expired / already-used link
  if (!sessionValid) {
    return (
      <motion.div {...pageMotion} style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', fontFamily: 'var(--font)' }}>
        <div style={{ textAlign: 'center', maxWidth: 360, padding: '0 24px' }}>
          <LogoMark size={40} />
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginTop: 20, marginBottom: 10 }}>
            Link invalid or expired
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 28 }}>
            This password reset link has expired or already been used. Please request a new one.
          </div>
          <button
            onClick={onDone}
            style={{
              padding: '10px 24px', borderRadius: 7, fontSize: 13, fontWeight: 600,
              border: '1px solid var(--accent)', background: 'var(--accent)',
              color: '#fff', fontFamily: 'var(--font)', cursor: 'pointer',
            }}
          >
            Back to sign in
          </button>
        </div>
      </motion.div>
    );
  }

  // Password successfully reset — sign-in prompt
  if (done) {
    return (
      <motion.div {...pageMotion} style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', fontFamily: 'var(--font)' }}>
        <div style={{ textAlign: 'center', maxWidth: 360, padding: '0 24px' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', margin: '0 auto 20px',
            background: 'var(--green-bg)', border: '1px solid var(--green)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
            Password updated
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 28 }}>
            Your password has been changed. Please sign in with your new password.
          </div>
          <button
            onClick={onDone}
            style={{
              padding: '10px 24px', borderRadius: 7, fontSize: 13, fontWeight: 600,
              border: '1px solid var(--accent)', background: 'var(--accent)',
              color: '#fff', fontFamily: 'var(--font)', cursor: 'pointer',
            }}
          >
            Sign in
          </button>
        </div>
      </motion.div>
    );
  }

  // Set new password form
  return (
    <motion.div {...pageMotion} style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', fontFamily: 'var(--font)', padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 6 }}>
            <LogoMark size={36} />
            <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>TheClearAudit</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Multi-framework compliance platform</div>
        </div>

        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '32px 28px',
        }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
              Set new password
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
              Choose a strong password for your account.
            </div>
          </div>

          {/* New password */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              New Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{ ...inputStyle, paddingRight: 52 }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 11, fontWeight: 500, fontFamily: 'var(--font)',
                  color: 'var(--text3)', padding: '2px 0', lineHeight: 1,
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>

            {/* Requirements checklist */}
            {password.length > 0 && (
              <div style={{
                marginTop: 10, padding: 12,
                background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontWeight: 500 }}>
                  Password requirements
                </div>
                {passwordChecks.map(req => (
                  <div key={req.label} style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, marginBottom: 5,
                    color: req.met ? 'var(--green)' : 'var(--text3)', transition: 'color 0.2s',
                  }}>
                    <div style={{
                      width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, fontWeight: 700, transition: 'all 0.2s',
                      background: req.met ? 'var(--green-bg)' : 'var(--surface2)',
                      color: req.met ? 'var(--green)' : 'var(--text3)',
                      border: `1px solid ${req.met ? 'var(--green)' : 'var(--border2)'}`,
                    }}>
                      {req.met ? '✓' : ''}
                    </div>
                    {req.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••••••"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={{
                ...inputStyle,
                borderColor: confirm && confirm !== password ? 'var(--red)' : undefined,
              }}
              onFocus={e => e.target.style.borderColor = confirm && confirm !== password ? 'var(--red)' : 'var(--accent)'}
              onBlur={e => e.target.style.borderColor = confirm && confirm !== password ? 'var(--red)' : 'var(--border2)'}
            />
            {confirm && confirm !== password && (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 5 }}>Passwords do not match.</div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: 'var(--red-bg)', border: '1px solid var(--red)',
              borderRadius: 6, padding: '10px 12px', fontSize: 12,
              color: 'var(--red)', marginBottom: 16,
            }}>{error}</div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: '100%', padding: '11px', borderRadius: 7, fontSize: 13, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              border: '1px solid var(--accent)', background: 'var(--accent)',
              color: '#fff', fontFamily: 'var(--font)',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Saving…' : 'Set New Password'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
